/**
 * Player Profile — everything about one player on one page (PREVIEW: admin only, enforced on the
 * server too).
 *
 * Each card fetches on its own and shows its own spinner and error, so one slow or failing part never
 * blanks the page. Live data and every action come from the game over the bridge; the snapshot list
 * and punishment timeline come straight from the game database (read only). Every name, item, reason
 * and home name is player-made, and is rendered as text only.
 */

import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatRelative, formatTimestamp } from "../format";
import {
  profileApi,
  readError,
  type BridgeAnswer,
  type InventoryData,
  type ProfileData,
  type PunishPreview,
  type Reason,
  type SnapshotData,
  type SnapshotRow,
  type TimelineEntry,
} from "../profileApi";
import { can, type Tier } from "../types";
import { confirmDialog } from "./Dialog";
import { EnderGrid, InventoryGrid, diffInventories } from "./ItemGrid";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ---- formatting ---------------------------------------------------------------------------------

const exact = (n: number | null | undefined): string => (typeof n === "number" ? Math.round(n).toLocaleString("en-GB") : "unknown");
function short(n: number | null | undefined): string {
  if (typeof n !== "number") return "";
  const abs = Math.abs(n);
  const [div, suffix] = abs >= 1e12 ? [1e12, "t"] : abs >= 1e9 ? [1e9, "b"] : abs >= 1e6 ? [1e6, "m"] : abs >= 1e3 ? [1e3, "k"] : [1, ""];
  return suffix ? `${(n / div).toFixed(1).replace(/\.0$/, "")}${suffix}` : "";
}
function duration(seconds: number | null | undefined): string {
  if (typeof seconds !== "number") return "unknown";
  const d = Math.floor(seconds / 86_400);
  const h = Math.floor((seconds % 86_400) / 3_600);
  const m = Math.floor((seconds % 3_600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}
const xyz = (p?: { x?: number; y?: number; z?: number } | null): string =>
  p && typeof p.x === "number" ? `${Math.round(p.x)}, ${Math.round(p.y ?? 0)}, ${Math.round(p.z ?? 0)}` : "unknown";
const show = (v: unknown): string => (v === null || v === undefined || v === "" ? "unknown" : String(v));

// ---- building blocks ----------------------------------------------------------------------------

function Card({
  title,
  loading,
  error,
  actions,
  children,
}: {
  title: string;
  loading?: boolean;
  error?: string | null;
  actions?: ReactNode;
  children?: ReactNode;
}): JSX.Element {
  return (
    <section className="profile-card">
      <header className="profile-card-head">
        <h2>{title}</h2>
        {loading && <span className="profile-spinner" aria-label="Loading" />}
        <span className="grow" />
        {actions}
      </header>
      {error ? <div className="login-error">{error}</div> : children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="profile-field">
      <span>{label}</span>
      <b>{children}</b>
    </div>
  );
}

/** Wraps a bridge read: data, or the user-facing error text. */
function unwrap<T>(answer: BridgeAnswer<T> | undefined, failure: unknown): { data: T | null; error: string | null } {
  if (failure) return { data: null, error: failure instanceof Error ? failure.message : "Could not load this." };
  if (!answer) return { data: null, error: null };
  return answer.ok ? { data: answer.data, error: null } : { data: null, error: readError(answer) };
}

// ---- search -------------------------------------------------------------------------------------

function ProfileSearch({ onOpen }: { onOpen: (id: string) => void }): JSX.Element {
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(timer);
  }, [term]);
  const results = useQuery({
    queryKey: ["profile-search", debounced],
    queryFn: ({ signal }) => profileApi.search(debounced, signal),
    enabled: debounced.length >= 2,
  });

  return (
    <div className="profile-search">
      <form
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          if (term.trim()) onOpen(term.trim());
        }}
      >
        <input
          className="input"
          autoFocus
          placeholder="Player name or UUID"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          aria-label="Find a player"
        />
        <button className="btn" data-variant="primary" type="submit" disabled={!term.trim()}>
          Open
        </button>
      </form>
      {results.isFetching && <p className="users-note">Searching…</p>}
      {results.data && results.data.players.length === 0 && <p className="users-note">Never joined.</p>}
      <div className="profile-results">
        {results.data?.players.map((p) => (
          <button key={p.uuid} type="button" className="profile-result" onClick={() => onOpen(p.uuid)}>
            <img src={`https://mc-heads.net/avatar/${p.uuid}/32`} alt="" width={32} height={32} loading="lazy" />
            <b>{p.name}</b>
            {p.online && <span className="profile-online-dot" title="Online now" />}
            <span className="grow" />
            <span className="mono users-note">{p.uuid.slice(0, 8)}…</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---- page ---------------------------------------------------------------------------------------

export interface ProfileTabProps {
  /** uuid, or a name until resolved. Empty shows the search. */
  profile: string;
  setProfile: (id: string) => void;
  tier: Tier;
  csrfToken: string;
  /** The most recent live punishment event, to refetch when it concerns this player. */
  lastPunishment: { uuid?: string; player?: string } | null;
}

export function ProfileTab(props: ProfileTabProps): JSX.Element {
  const { profile, setProfile } = props;
  const [neverJoined, setNeverJoined] = useState<string | null>(null);

  // Canonicalise /players/<name> to /players/<uuid>.
  useEffect(() => {
    setNeverJoined(null);
    if (!profile || UUID.test(profile)) return;
    let cancelled = false;
    profileApi
      .resolve(profile)
      .then((found) => !cancelled && setProfile(found.uuid))
      .catch(() => !cancelled && setNeverJoined(profile));
    return () => {
      cancelled = true;
    };
  }, [profile, setProfile]);

  if (!profile) return <ProfileSearch onOpen={setProfile} />;
  if (neverJoined) {
    return (
      <>
        <ProfileSearch onOpen={setProfile} />
        <div className="login-error">{neverJoined} has never joined.</div>
      </>
    );
  }
  if (!UUID.test(profile)) return <p className="users-note">Finding {profile}…</p>;
  return <ProfilePage {...props} uuid={profile.toLowerCase()} />;
}

function ProfilePage({ uuid, tier, csrfToken, lastPunishment, setProfile }: ProfileTabProps & { uuid: string }): JSX.Element {
  const queryClient = useQueryClient();
  const staff = can(tier, "portal");
  const admin = can(tier, "admin");

  const profile = useQuery({
    queryKey: ["profile", uuid],
    queryFn: () => profileApi.profile(uuid),
    // Every 5 s while they are online and this tab is visible (react-query pauses when hidden).
    refetchInterval: (query) => {
      const answer = query.state.data;
      return answer && answer.ok && answer.data.online ? 5_000 : 30_000;
    },
  });
  const p = unwrap<ProfileData>(profile.data, profile.error);
  const name = profile.data?.name ?? p.data?.name ?? uuid;

  const inventory = useQuery({ queryKey: ["profile-inv", uuid], queryFn: () => profileApi.inventory(uuid), enabled: staff });
  const inv = unwrap<InventoryData>(inventory.data, inventory.error);

  // Live punishment channel: refetch (never patch) when it concerns this player.
  useEffect(() => {
    if (!lastPunishment) return;
    const sameUuid = lastPunishment.uuid?.toLowerCase() === uuid;
    const sameName = Boolean(lastPunishment.player && name && lastPunishment.player.toLowerCase() === String(name).toLowerCase());
    if (sameUuid || sameName) {
      void queryClient.invalidateQueries({ queryKey: ["profile-punishments", uuid] });
      void queryClient.invalidateQueries({ queryKey: ["profile", uuid] });
    }
  }, [lastPunishment, uuid, name, queryClient]);

  const refreshAfterAction = (): void => {
    for (const key of ["profile-punishments", "profile", "profile-actions", "profile-inv", "profile-snapshots"]) {
      void queryClient.invalidateQueries({ queryKey: [key, uuid] });
    }
  };

  return (
    <div className="profile">
      <button type="button" className="btn home-btn profile-back" onClick={() => setProfile("")}>
        ‹ Find another player
      </button>
      <HeaderCard uuid={uuid} name={String(name)} data={p.data} error={p.error} loading={profile.isFetching} />
      <LiveStateCard data={p.data} error={p.error} inv={inv} invLoading={inventory.isFetching} staff={staff} onRefreshInventory={() => void inventory.refetch()} />
      <ConnectionCard data={p.data} error={p.error} />
      {staff && <SnapshotsCard uuid={uuid} name={String(name)} admin={admin} csrfToken={csrfToken} current={inv.data} online={Boolean(p.data?.online)} serverLabel={p.data?.server?.label ?? null} onDone={refreshAfterAction} />}
      <PunishmentsCard uuid={uuid} name={String(name)} staff={staff} admin={admin} csrfToken={csrfToken} onDone={refreshAfterAction} />
      {staff && <ActionLogCard uuid={uuid} />}
    </div>
  );
}

// ---- 2.1 header ---------------------------------------------------------------------------------

function HeaderCard({ uuid, name, data, error, loading }: { uuid: string; name: string; data: ProfileData | null; error: string | null; loading: boolean }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <section className="profile-card profile-header">
      <img className="profile-head" src={`https://mc-heads.net/avatar/${uuid}/64`} alt="" width={64} height={64} />
      <div className="profile-header-main">
        <h1>{name}</h1>
        <div className="profile-uuid">
          <span className="mono">{uuid}</span>
          <button
            type="button"
            className="btn"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(uuid);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              } catch {
                // Clipboard blocked; the uuid is on screen to select by hand.
              }
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>
          {loading && <span className="profile-spinner" aria-label="Loading" />}
        </div>
        {error && <div className="login-error">{error}</div>}
        {data && (
          <div className="profile-pills">
            {data.online ? (
              <span className="profile-pill" data-tone="ok">
                Online on {show(data.server?.label ?? data.server?.id)}
                {data.proxy ? ` via ${data.proxy}` : ""}
              </span>
            ) : (
              <span className="profile-pill">
                Offline{data.lastSeen ? ` · last seen ${formatRelative(data.lastSeen)}` : ""}
                {data.lastServer?.label ? ` on ${data.lastServer.label}` : ""}
              </span>
            )}
            {data.banned && <span className="profile-pill" data-tone="danger">Banned</span>}
            {data.muted && <span className="profile-pill" data-tone="warn">Muted</span>}
            {data.staffMode && <span className="profile-pill" data-tone="info">Staff mode</span>}
            {data.rank?.primary && <span className="profile-pill">{data.rank.primary}</span>}
            {(data.rank?.groups ?? []).filter((g) => g !== data.rank?.primary).map((g) => (
              <span key={g} className="profile-chip">{g}</span>
            ))}
          </div>
        )}
        {data?.online && (
          <div className="profile-fields">
            <Field label="World">{show(data.location?.world)} · {xyz(data.location)}</Field>
            <Field label="Ping">{data.ping !== undefined ? `${data.ping} ms` : "unknown"}</Field>
            <Field label="Game mode">{show(data.gameMode)}</Field>
            <Field label="Health">{data.health !== undefined ? `${Math.round(data.health)} / ${Math.round(data.maxHealth ?? 20)}` : "unknown"}</Field>
            <Field label="Food">{show(data.food)}</Field>
            <Field label="Level">{show(data.level)}</Field>
          </div>
        )}
        {data && !data.online && data.lastLogout && (
          <div className="profile-fields">
            <Field label="Logged out at">
              {show(data.lastLogout.world)} · {xyz(data.lastLogout)} {data.lastLogout.at ? `· ${formatTimestamp(data.lastLogout.at)}` : ""}
            </Field>
          </div>
        )}
        {data && (
          <div className="profile-fields">
            <Field label="First seen">{data.firstSeen ? formatTimestamp(data.firstSeen) : "unknown"}</Field>
            <Field label="Last seen">{data.lastSeen ? formatTimestamp(data.lastSeen) : data.online ? "now" : "unknown"}</Field>
          </div>
        )}
      </div>
    </section>
  );
}

// ---- 2.2 live state -----------------------------------------------------------------------------

function LiveStateCard({
  data,
  error,
  inv,
  invLoading,
  staff,
  onRefreshInventory,
}: {
  data: ProfileData | null;
  error: string | null;
  inv: { data: InventoryData | null; error: string | null };
  invLoading: boolean;
  staff: boolean;
  onRefreshInventory: () => void;
}): JSX.Element {
  const [filter, setFilter] = useState("");
  const stats = data?.stats ?? {};
  const kd = typeof stats.kills === "number" && typeof stats.deaths === "number" ? (stats.deaths ? stats.kills / stats.deaths : stats.kills).toFixed(2) : "unknown";

  return (
    <>
      <Card title="Balances & stats" error={error}>
        {data && (
          <>
            <div className="profile-fields">
              <Field label="Money">{exact(data.balances?.MONEY)} <small>{short(data.balances?.MONEY)}</small></Field>
              <Field label="Prisms">{exact(data.balances?.PRISMS)} <small>{short(data.balances?.PRISMS)}</small></Field>
              <Field label="Kills">{exact(stats.kills)}</Field>
              <Field label="Deaths">{exact(stats.deaths)}</Field>
              <Field label="K/D">{kd}</Field>
              <Field label="Playtime">{duration(stats.playtimeSeconds)}</Field>
              <Field label="Blocks broken">{exact(stats.blocksBroken)}</Field>
              <Field label="Blocks placed">{exact(stats.blocksPlaced)}</Field>
              <Field label="Earned from /sell">{exact(stats.sellEarned)}</Field>
              <Field label="Spent in shop">{exact(stats.shopSpent)}</Field>
            </div>
            <h3 className="profile-sub">Crate keys</h3>
            <div className="profile-pills">
              {Object.entries(data.crateKeys ?? {}).length === 0 && <span className="users-note">None</span>}
              {Object.entries(data.crateKeys ?? {}).map(([crate, count]) => (
                <span key={crate} className="profile-chip">{crate} × {count}</span>
              ))}
            </div>
            <h3 className="profile-sub">Team</h3>
            <p>{data.team ? `${data.team.name} (${show(data.team.role)})` : "No team"}</p>
          </>
        )}
      </Card>

      <Card title="Settings & homes" error={error}>
        {data && (
          <>
            <div className="tablewrap">
              <table>
                <thead><tr><th>Setting</th><th>Value</th></tr></thead>
                <tbody>
                  {(data.settings ?? []).length === 0 && <tr><td colSpan={2} className="empty">unknown</td></tr>}
                  {(data.settings ?? []).map((s, i) => (
                    <tr key={s.id ?? i}>
                      <td>{show(s.name ?? s.id)}</td>
                      <td>
                        {typeof s.value === "boolean" ? (s.value ? "ON" : "OFF") : show(s.value)}
                        {s.default && <span className="profile-chip">default</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h3 className="profile-sub">Homes</h3>
            <div className="tablewrap">
              <table>
                <thead><tr><th>#</th><th>Name</th><th>Server</th><th>World</th><th>Position</th></tr></thead>
                <tbody>
                  {(data.homes ?? []).length === 0 && <tr><td colSpan={5} className="empty">No homes</td></tr>}
                  {(data.homes ?? []).map((h, i) => (
                    <tr key={i}>
                      <td>{show(h.slot)}</td><td>{show(h.name)}</td><td>{show(h.serverId)}</td><td>{show(h.world)}</td><td className="mono">{xyz(h)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {staff && (
        <Card
          title="Current inventory"
          loading={invLoading}
          error={inv.error}
          actions={
            <>
              <input className="input" placeholder="Find an item" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Find an item" />
              <button className="btn" type="button" onClick={onRefreshInventory} disabled={invLoading}>Refresh</button>
            </>
          }
        >
          {inv.data && (
            <>
              <p className="users-note">
                {inv.data.source === "live" ? "Live (online now)" : `Last saved ${inv.data.savedAt ? formatRelative(inv.data.savedAt) : "at an unknown time"} (offline)`}
              </p>
              <InventoryGrid grid={inv.data.inventory} filter={filter} />
              <h3 className="profile-sub">Ender chest</h3>
              <EnderGrid items={inv.data.enderChest} filter={filter} />
            </>
          )}
        </Card>
      )}
    </>
  );
}

// ---- 2.3 connection -----------------------------------------------------------------------------

function ConnectionCard({ data, error }: { data: ProfileData | null; error: string | null }): JSX.Element {
  return (
    <Card title="Connection" error={error}>
      {data && (
        <div className="profile-fields">
          <Field label="Status">{data.online ? "Online" : "Offline"}</Field>
          <Field label="Server">{show(data.server?.label ?? data.server?.id)}</Field>
          <Field label="Proxy">{show(data.proxy)}</Field>
          <Field label="Last server">{show(data.lastServer?.label ?? data.lastServer?.id)}</Field>
          <Field label="Last logout">
            {data.lastLogout ? `${show(data.lastLogout.world)} · ${xyz(data.lastLogout)}${data.lastLogout.at ? ` · ${formatTimestamp(data.lastLogout.at)}` : ""}` : "unknown"}
          </Field>
          <Field label="IP lock">{data.ipLocked === undefined ? "unknown" : data.ipLocked ? "On" : "Off"}</Field>
        </div>
      )}
    </Card>
  );
}

// ---- 2.4 / 2.5 snapshots ------------------------------------------------------------------------

const KNOWN_REASONS = ["JOIN", "LEAVE", "DEATH", "WIPE"];

function SnapshotsCard({
  uuid,
  name,
  admin,
  csrfToken,
  current,
  online,
  serverLabel,
  onDone,
}: {
  uuid: string;
  name: string;
  admin: boolean;
  csrfToken: string;
  current: InventoryData | null;
  online: boolean;
  serverLabel: string | null;
  onDone: () => void;
}): JSX.Element {
  const [reasons, setReasons] = useState<string[]>([]);
  const [server, setServer] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [pages, setPages] = useState<SnapshotRow[][]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [open, setOpen] = useState<SnapshotRow | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    for (const r of reasons) params.append("reason", r);
    if (server) params.set("server", server);
    if (from) params.set("from", String(Date.parse(from)));
    if (to) params.set("to", String(Date.parse(to) + 86_399_999));
    return params.toString();
  }, [reasons, server, from, to]);

  const first = useQuery({ queryKey: ["profile-snapshots", uuid, query], queryFn: () => profileApi.snapshots(uuid, query, null) });
  useEffect(() => {
    setPages([]);
    setCursor(first.data?.nextCursor ?? null);
  }, [first.data]);
  const rows = [...(first.data?.snapshots ?? []), ...pages.flat()];

  const loadMore = async (): Promise<void> => {
    if (!cursor) return;
    const next = await profileApi.snapshots(uuid, query, cursor);
    setPages((p) => [...p, next.snapshots]);
    setCursor(next.nextCursor);
  };

  const toggle = (reason: string): void =>
    setReasons((current) => (current.includes(reason) ? current.filter((r) => r !== reason) : [...current, reason]));

  return (
    <Card title="Snapshot history" loading={first.isFetching} error={first.error instanceof Error ? first.error.message : null}>
      <div className="filterbar profile-filters">
        {KNOWN_REASONS.map((r) => (
          <button key={r} type="button" className="btn" data-active={reasons.includes(r)} onClick={() => toggle(r)}>{r}</button>
        ))}
        <button type="button" className="btn" data-active={reasons.length === 1 && reasons[0] === "WIPE"} onClick={() => setReasons(["WIPE"])}>Pre-wipe only</button>
        <select className="input" value={server} onChange={(e) => setServer(e.target.value)} aria-label="Server">
          <option value="">All servers</option>
          {(first.data?.servers ?? []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
        <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
      </div>
      <div className="tablewrap">
        <table>
          <thead><tr><th>When</th><th>Reason</th><th>Server</th><th>Mode</th><th>Health</th><th>Food</th><th>Level</th><th>Money</th><th>Prisms</th></tr></thead>
          <tbody>
            {first.data && rows.length === 0 && <tr><td colSpan={9} className="empty">No snapshots.</td></tr>}
            {rows.map((row) => (
              <tr key={row.id} className="portal-row" data-wipe={row.reason === "WIPE"} onClick={() => setOpen(row)} style={{ cursor: "pointer" }}>
                <td className="mono" title={formatTimestamp(row.timestamp)}>{formatRelative(row.timestamp)}</td>
                <td><span className="profile-reason" data-reason={row.reason ?? ""}>{show(row.reason)}</span></td>
                <td>{show(row.server)}</td>
                <td>{show(row.gamemode)}</td>
                <td>{row.health === null ? "-" : Math.round(row.health)}</td>
                <td>{show(row.food)}</td>
                <td>{show(row.level)}</td>
                <td>{exact(row.money)}</td>
                <td>{exact(row.prisms)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cursor && <button className="btn" type="button" onClick={() => void loadMore()}>Load 50 more</button>}
      {open && (
        <SnapshotModal
          uuid={uuid}
          name={name}
          row={open}
          admin={admin}
          csrfToken={csrfToken}
          current={current}
          online={online}
          serverLabel={serverLabel}
          onClose={() => setOpen(null)}
          onDone={onDone}
        />
      )}
    </Card>
  );
}

const ROLLBACKS: Array<{ scope: string; label: string; replaces: string; typed?: boolean }> = [
  { scope: "INVENTORY", label: "Inventory only", replaces: "their inventory" },
  { scope: "ENDERCHEST", label: "Ender chest", replaces: "their ender chest" },
  { scope: "HOMES", label: "Homes", replaces: "their homes" },
  { scope: "MONEY", label: "Money", replaces: "their Money balance" },
  { scope: "ORBS", label: "Prisms", replaces: "their Prisms balance" },
  { scope: "EVERYTHING_NO_ECON", label: "Unwipe (without economy)", replaces: "everything except Money and Prisms", typed: true },
  { scope: "EVERYTHING", label: "Unwipe (with economy)", replaces: "everything, including Money and Prisms", typed: true },
];

function SnapshotModal({
  uuid,
  name,
  row,
  admin,
  csrfToken,
  current,
  online,
  serverLabel,
  onClose,
  onDone,
}: {
  uuid: string;
  name: string;
  row: SnapshotRow;
  admin: boolean;
  csrfToken: string;
  current: InventoryData | null;
  online: boolean;
  serverLabel: string | null;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const snapshot = useQuery({ queryKey: ["profile-snapshot", uuid, row.timestamp], queryFn: () => profileApi.snapshot(uuid, row.timestamp) });
  const s = unwrap<SnapshotData>(snapshot.data, snapshot.error);
  const [compare, setCompare] = useState(false);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [typed, setTyped] = useState<(typeof ROLLBACKS)[number] | null>(null);

  const diffs = useMemo(() => (compare && s.data && current ? diffInventories(s.data, current) : undefined), [compare, s.data, current]);
  const meta = s.data?.meta ?? {};

  const run = async (entry: (typeof ROLLBACKS)[number]): Promise<void> => {
    setBusy(true);
    setResult(null);
    const answer = await profileApi.act(uuid, "rollback", { timestamp: row.timestamp, scope: entry.scope }, csrfToken);
    setBusy(false);
    setResult(answer.ok ? { ok: true, text: answer.data.summary ?? "Done." } : { ok: false, text: answer.message ?? readError(answer) });
    onDone();
  };

  const confirmRollback = async (entry: (typeof ROLLBACKS)[number]): Promise<void> => {
    if (entry.typed) {
      setTyped(entry);
      return;
    }
    const ok = await confirmDialog({
      title: `Roll back ${entry.label.toLowerCase()}?`,
      message: `Replaces ${entry.replaces} with this snapshot from ${formatTimestamp(row.timestamp)}. ${online ? `Applies now${serverLabel ? ` on ${serverLabel}` : ""}.` : "Applies when they next join."}`,
      confirmLabel: `Roll back ${entry.label.toLowerCase()}`,
      danger: true,
    });
    if (ok) await run(entry);
  };

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div className="dialog profile-snapshot" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="Snapshot">
        <header className="profile-card-head">
          <h2>
            {show(row.reason)} · {formatTimestamp(row.timestamp)} <small className="users-note">({formatRelative(row.timestamp)})</small>
          </h2>
          {snapshot.isFetching && <span className="profile-spinner" />}
          <span className="grow" />
          <button className="btn" type="button" onClick={onClose}>Close</button>
        </header>
        {s.error && <div className="login-error">{s.error}</div>}
        {s.data && (
          <>
            <div className="profile-fields">
              <Field label="Server">{show(meta.server ?? row.server)}</Field>
              <Field label="Position">{show(meta.world)} · {show(meta.location)}</Field>
              <Field label="Game mode">{show(meta.gamemode ?? row.gamemode)}</Field>
              <Field label="Health">{show(meta.health ?? row.health)}</Field>
              <Field label="Food">{show(meta.food ?? row.food)}</Field>
              <Field label="Level / XP">{show(meta.level ?? row.level)} / {show(meta.xp)}</Field>
              <Field label="Air">{show(meta.air)}</Field>
              <Field label="Playtime">{show(meta.playtime)}</Field>
              <Field label="Money">{exact(Number(meta.money ?? row.money))}</Field>
              <Field label="Prisms">{exact(Number(meta.shards ?? meta.prisms ?? row.prisms))}</Field>
            </div>
            <div className="filterbar profile-filters">
              <label className="switch">
                <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} disabled={!current} />
                Compare with current
              </label>
              {compare && <span className="users-note"><b style={{ color: "var(--ok)" }}>green</b> = would come back · <b style={{ color: "var(--danger)" }}>red</b> = would be removed · <b style={{ color: "var(--warn)" }}>amber</b> = different amount</span>}
              <span className="grow" />
              <input className="input" placeholder="Find an item" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Find an item" />
            </div>
            <InventoryGrid grid={s.data.inventory} filter={filter} diffs={diffs} />
            <h3 className="profile-sub">Ender chest</h3>
            <EnderGrid items={s.data.enderChest} filter={filter} diffs={diffs} />
            <h3 className="profile-sub">Potion effects</h3>
            <p>
              {(s.data.effects ?? []).length === 0
                ? "None"
                : (s.data.effects ?? []).map((e) => `${show(e.type)} ${(e.amplifier ?? 0) + 1} (${duration(e.secondsLeft)})`).join(" · ")}
            </p>
            <h3 className="profile-sub">Homes</h3>
            <p>{(s.data.homes ?? []).length === 0 ? "None" : (s.data.homes ?? []).map((h) => show(h.name)).join(", ")}</p>
            {Object.entries(s.data.extras ?? {}).map(([section, value]) => (
              <details key={section} className="profile-extra">
                <summary>{section}</summary>
                <pre>{JSON.stringify(value, null, 2)}</pre>
              </details>
            ))}

            {admin && (
              <>
                <h3 className="profile-sub">Roll back to this snapshot</h3>
                <p className="users-note">{online ? `${name} is online — changes apply now${serverLabel ? ` on ${serverLabel}` : ""}.` : `${name} is offline — changes apply when they next join.`}</p>
                <div className="profile-pills">
                  {ROLLBACKS.map((entry) => (
                    <button key={entry.scope} type="button" className="btn" data-variant={entry.typed ? "danger" : undefined} disabled={busy} onClick={() => void confirmRollback(entry)}>
                      {entry.label}
                    </button>
                  ))}
                </div>
              </>
            )}
            {result && <div className={result.ok ? "evidence-ok" : "login-error"}>{result.text}</div>}
          </>
        )}
        {typed && (
          <TypedConfirm
            title={typed.label}
            message={`Replaces ${typed.replaces} with this snapshot from ${formatTimestamp(row.timestamp)}. ${online ? "Applies now." : "Applies when they next join."}`}
            name={name}
            confirmLabel={typed.label}
            onCancel={() => setTyped(null)}
            onConfirm={() => {
              const entry = typed;
              setTyped(null);
              void run(entry);
            }}
          />
        )}
      </div>
    </div>
  );
}

/** For the irreversible-feeling actions: type the player's name to enable the button. */
function TypedConfirm({
  title,
  message,
  name,
  confirmLabel,
  onCancel,
  onConfirm,
}: {
  title: string;
  message: string;
  name: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const [value, setValue] = useState("");
  return (
    <div className="dialog-backdrop" onMouseDown={onCancel}>
      <div className="dialog" data-danger="true" onMouseDown={(e) => e.stopPropagation()} role="alertdialog">
        <div className="dialog-icon">!</div>
        <h2>{title}</h2>
        <p>{message}</p>
        <p>
          Type <b>{name}</b> to confirm.
        </p>
        <input className="input" autoFocus value={value} onChange={(e) => setValue(e.target.value)} aria-label="Player name" />
        <div className="dialog-buttons" style={{ marginTop: 14 }}>
          <button className="btn" type="button" onClick={onCancel}>Cancel</button>
          <button className="btn" type="button" data-variant="danger" disabled={value.trim().toLowerCase() !== name.toLowerCase()} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- 2.6 punishments ----------------------------------------------------------------------------

function PunishmentsCard({
  uuid,
  name,
  staff,
  admin,
  csrfToken,
  onDone,
}: {
  uuid: string;
  name: string;
  staff: boolean;
  admin: boolean;
  csrfToken: string;
  onDone: () => void;
}): JSX.Element {
  const timeline = useQuery({ queryKey: ["profile-punishments", uuid], queryFn: () => profileApi.punishments(uuid) });
  const [modal, setModal] = useState<"punish" | "custom" | "wipe" | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const act = async (op: string, args: Record<string, unknown>): Promise<void> => {
    setBusy(true);
    setResult(null);
    const answer = await profileApi.act(uuid, op, args, csrfToken);
    setBusy(false);
    setResult(
      answer.ok
        ? { ok: true, text: `${answer.data.summary ?? "Done."}${answer.data.appliedNow === false ? " (applies when they next join)" : ""}` }
        : { ok: false, text: answer.message ?? readError(answer) },
    );
    onDone();
  };

  const lift = async (entry: TimelineEntry): Promise<void> => {
    const ok = await confirmDialog({
      title: `${entry.kind === "ban" ? "Unban" : "Unmute"} ${name}?`,
      message: entry.protected ? "This ban is protected — only sr-admin+ can lift it. The game will decide." : `Lifts the active ${entry.kind}: “${entry.reason}”.`,
      confirmLabel: entry.kind === "ban" ? "Unban" : "Unmute",
    });
    if (ok) await act(entry.kind === "ban" ? "unban" : "unmute", {});
  };

  return (
    <Card
      title="Punishments"
      loading={timeline.isFetching}
      error={timeline.error instanceof Error ? timeline.error.message : null}
      actions={
        staff && (
          <>
            <button className="btn" data-variant="primary" type="button" disabled={busy} onClick={() => setModal("punish")}>Punish</button>
            {admin && <button className="btn" type="button" disabled={busy} onClick={() => setModal("custom")}>Custom ban / mute</button>}
            {admin && <button className="btn" type="button" data-variant="danger" disabled={busy} onClick={() => setModal("wipe")}>Wipe</button>}
          </>
        )
      }
    >
      {result && <div className={result.ok ? "evidence-ok" : "login-error"}>{result.text}</div>}
      <div className="tablewrap">
        <table>
          <thead><tr><th>Type</th><th>Reason</th><th>By</th><th>Issued</th><th>Expires</th><th>State</th><th /></tr></thead>
          <tbody>
            {timeline.data && timeline.data.timeline.length === 0 && <tr><td colSpan={7} className="empty">No punishments.</td></tr>}
            {timeline.data?.timeline.map((entry, i) => (
              <tr key={`${entry.kind}:${entry.id ?? i}:${entry.issuedAt}`}>
                <td>
                  <span className="portal-kind" data-kind={entry.kind}>{entry.kind}</span>
                  {entry.ipBan && <span className="profile-chip">IP</span>}
                  {entry.protected && <span title="Only sr-admin+ can lift this" aria-label="Protected"> 🔒</span>}
                </td>
                <td style={{ whiteSpace: "normal" }}>{entry.reason}</td>
                <td>{entry.issuer}</td>
                <td className="mono">{formatTimestamp(entry.issuedAt)}</td>
                <td className="mono">{entry.kind === "kick" ? "-" : entry.expiresAt ? formatTimestamp(entry.expiresAt) : "Permanent"}</td>
                <td><span className="profile-state" data-state={entry.state}>{entry.state}</span></td>
                <td style={{ textAlign: "right" }}>
                  {staff && entry.state === "Active" && entry.kind !== "kick" && (
                    <button className="btn" type="button" disabled={busy} onClick={() => void lift(entry)}>
                      {entry.kind === "ban" ? (entry.protected ? "Unban (needs sr-admin+)" : "Unban") : "Unmute"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal === "punish" && <PunishModal uuid={uuid} name={name} busy={busy} onClose={() => setModal(null)} onConfirm={(reason) => { setModal(null); void act("punish", { reason }); }} />}
      {modal === "custom" && <CustomModal name={name} busy={busy} onClose={() => setModal(null)} onConfirm={(op, args) => { setModal(null); void act(op, args); }} />}
      {modal === "wipe" && (
        <TypedConfirm
          title={`Wipe ${name}?`}
          message="A WIPE snapshot is taken first, so this can be undone from the snapshot history."
          name={name}
          confirmLabel="Wipe"
          onCancel={() => setModal(null)}
          onConfirm={() => {
            setModal(null);
            void act("wipe", {});
          }}
        />
      )}
    </Card>
  );
}

function PunishModal({ uuid, name, busy, onClose, onConfirm }: { uuid: string; name: string; busy: boolean; onClose: () => void; onConfirm: (reason: string) => void }): JSX.Element {
  const reasons = useQuery({ queryKey: ["profile-reasons"], queryFn: profileApi.reasons });
  const list = unwrap<Reason[]>(reasons.data, reasons.error);
  const [reason, setReason] = useState("");
  const preview = useQuery({ queryKey: ["profile-preview", uuid, reason], queryFn: () => profileApi.preview(uuid, reason), enabled: Boolean(reason) });
  const pv = unwrap<PunishPreview>(preview.data, preview.error);
  const outcome = pv.data
    ? `${pv.data.type === "ban" ? "Ban" : "Mute"} ${pv.data.duration === "perm" ? "PERMANENTLY" : `for ${pv.data.duration}`}`
    : null;

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div className="dialog" onMouseDown={(e) => e.stopPropagation()} role="dialog">
        <h2>Punish {name}</h2>
        {list.error && <div className="login-error">{list.error}</div>}
        <select className="input" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason" style={{ width: "100%" }}>
          <option value="">Choose a reason…</option>
          {(["ban", "mute"] as const).map((type) => (
            <optgroup key={type} label={type === "ban" ? "Ban" : "Mute"}>
              {(list.data ?? []).filter((r) => r.type === type).map((r) => (
                <option key={r.key} value={r.key}>{r.key}{r.baseDuration ? ` (${r.baseDuration})` : " (perm)"}</option>
              ))}
            </optgroup>
          ))}
        </select>
        {preview.isFetching && <p className="users-note">Asking the game…</p>}
        {pv.error && <div className="login-error">{pv.error}</div>}
        {pv.data && (
          <div className="profile-preview" data-perm={pv.data.duration === "perm"}>
            Offence #{pv.data.offence} for {reason} → <b>{outcome}</b>
            {pv.data.wipe && " · wipes their account"}
            {pv.data.ipBan && " · IP ban"}
            {pv.data.alreadyActive && <div className="login-error">Already {pv.data.type === "ban" ? "banned" : "muted"}.</div>}
          </div>
        )}
        <div className="dialog-buttons" style={{ marginTop: 14 }}>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn"
            type="button"
            data-variant="danger"
            disabled={busy || !pv.data || pv.data.alreadyActive}
            onClick={() => onConfirm(reason)}
          >
            {outcome ?? "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomModal({ name, busy, onClose, onConfirm }: { name: string; busy: boolean; onClose: () => void; onConfirm: (op: "ban" | "mute", args: Record<string, unknown>) => void }): JSX.Element {
  const [type, setType] = useState<"ban" | "mute">("ban");
  const [length, setLength] = useState("7d");
  const [reason, setReason] = useState("");
  const valid = /^(?:\d{1,4}[mhdw]|perm)$/.test(length) && reason.trim().length > 0;
  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div className="dialog" onMouseDown={(e) => e.stopPropagation()} role="dialog">
        <h2>Custom {type} for {name}</h2>
        <p className="users-note">A custom reason does not count towards the offence ladder unless it matches a preset.</p>
        <div className="profile-pills" style={{ justifyContent: "center", marginBottom: 10 }}>
          <button type="button" className="btn" data-active={type === "ban"} onClick={() => setType("ban")}>Ban</button>
          <button type="button" className="btn" data-active={type === "mute"} onClick={() => setType("mute")}>Mute</button>
        </div>
        <select className="input" value={length} onChange={(e) => setLength(e.target.value)} aria-label="Duration" style={{ width: "100%", marginBottom: 8 }}>
          {["30m", "1h", "12h", "1d", "3d", "7d", "14d", "30d", "perm"].map((d) => (
            <option key={d} value={d}>{d === "perm" ? "Permanent" : d}</option>
          ))}
        </select>
        <input className="input" placeholder="Reason" value={reason} maxLength={255} onChange={(e) => setReason(e.target.value)} style={{ width: "100%" }} aria-label="Reason" />
        <div className="dialog-buttons" style={{ marginTop: 14 }}>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn" type="button" data-variant="danger" disabled={busy || !valid} onClick={() => onConfirm(type, { duration: length, reason: reason.trim() })}>
            {type === "ban" ? "Ban" : "Mute"} {length === "perm" ? "permanently" : `for ${length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- 2.7 action log -----------------------------------------------------------------------------

function ActionLogCard({ uuid }: { uuid: string }): JSX.Element {
  const actions = useQuery({ queryKey: ["profile-actions", uuid], queryFn: () => profileApi.actions(uuid) });
  return (
    <Card title="Action log" loading={actions.isFetching} error={actions.error instanceof Error ? actions.error.message : null}>
      <div className="tablewrap">
        <table>
          <thead><tr><th>When</th><th>Who</th><th>What</th></tr></thead>
          <tbody>
            {actions.data && actions.data.actions.length === 0 && <tr><td colSpan={3} className="empty">No actions from this website yet.</td></tr>}
            {actions.data?.actions.map((a) => (
              <tr key={a.id}>
                <td className="mono">{formatTimestamp(a.at)}</td>
                <td>{a.by}</td>
                <td className="mono" style={{ whiteSpace: "normal" }}>{a.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
